import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { authService } from '@/services/auth/authService';
import { worktaskKeys } from './useWorkTask';
import { inboxKeys } from './useTaskInbox';
import { setTaskInboxRealtimeConnected } from './taskInboxRealtime';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050';
const HUB_URL = `${API_BASE_URL}/signalr/task-inbox`;

/** The hub's one event. Its `Change` field says which transition; the inbox refreshes for all. */
const TASK_INBOX_CHANGED = 'TaskInboxChanged';

/**
 * App-global subscriber for the task inbox channel (TaskInboxHub). Mounted once in Layout.
 *
 * The inbox used to ask the server every 30 seconds whether anything had happened — for every
 * signed-in user, forever, and still up to 30 seconds late. The server now says so: claiming,
 * completing, cancelling, a workflow raising a task, a deadline passing all push here, and the
 * inbox list, its badge and a record's decision banner refresh from one signal.
 *
 * Every event does the same thing, because the payload is an invalidation signal and not the
 * data (see ITaskNotificationService) — the refetch it triggers is what applies the caller's
 * permissions to what they may actually see.
 */
export function useTaskInboxSignalR() {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!authService.isAuthenticated()) return;

    const tenantId = authService.getTenantId();
    let stopped = false;

    const connection = new HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: () => authService.getToken() || '',
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    const refreshInbox = () => {
      // The inbox list, the nav badge and the per-record tasks all hang off inboxKeys; a task's
      // own detail view hangs off the generated worktask keys.
      queryClient.invalidateQueries({ queryKey: inboxKeys.all(tenantId) });
      queryClient.invalidateQueries({ queryKey: worktaskKeys.all(tenantId) });
    };

    connection.on(TASK_INBOX_CHANGED, refreshInbox);

    const subscribe = () =>
      connection.invoke('SubscribeToInbox').catch((err: unknown) => {
        console.error('[TaskInboxSignalR] SubscribeToInbox failed:', err);
      });

    connection.onreconnecting(() => setTaskInboxRealtimeConnected(false));

    connection.onclose(() => setTaskInboxRealtimeConnected(false));

    // A reconnect is a new connection id, so the groups are gone — rejoin them, and refresh:
    // whatever happened while the socket was down was pushed to nobody.
    connection.onreconnected(() => {
      setTaskInboxRealtimeConnected(true);
      subscribe();
      refreshInbox();
    });

    // Defer the connect by a tick. React's StrictMode mounts an effect, tears it down and
    // mounts it again; without this the throwaway first mount opens a socket that its own
    // cleanup aborts mid-negotiation, and the SignalR client logs that as a failed connection —
    // two red errors in the console on every reload, for nothing that failed. By the time this
    // fires, a discarded mount has already set `stopped`.
    const startTimer = setTimeout(() => {
      connection
        .start()
        .then(() => {
          if (stopped) return;
          setTaskInboxRealtimeConnected(true);
          subscribe();
        })
        .catch((err) => {
          // Still possible for a real teardown mid-negotiation (a navigation away): that is
          // our own doing, not a connection that failed.
          if (stopped) return;

          console.error('[TaskInboxSignalR] Connection failed:', err);
          setTaskInboxRealtimeConnected(false);
        });
    }, 0);

    return () => {
      stopped = true;
      clearTimeout(startTimer);
      setTaskInboxRealtimeConnected(false);
      connection.off(TASK_INBOX_CHANGED, refreshInbox);
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop().catch(() => {});
      }
    };
  }, [queryClient]);
}
